import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { type ReactNode, useMemo } from 'react';
import { AppShell } from '../../components/AppShell.js';
import { useScopeSelection } from '../../components/filters/useScopeSelection.js';
import { availableKpis } from '../../components/media/data/utils.js';
import { ReportScopeProvider } from '../../components/reports/ReportScope.js';
import { ReportsFilterBar } from '../../components/reports/ReportsFilterBar.js';
import { dateWindowFromRange } from '../../lib/dateUtils.js';
import { useMetrics } from '../../lib/filters.hooks.js';
import { useReportsSearch, validateReportsSearch } from '../../lib/reports-search.js';
import { useAuthenticatedSession } from '../../lib/useAuthenticatedSession.js';
import { useShellAccount } from '../../lib/useShellAccount.js';

function ReportsLayout(): ReactNode {
  const today = useMemo(() => new Date(), []);
  const { session, isPending, onSignOut } = useAuthenticatedSession();
  const account = useShellAccount(session?.user.email);
  const search = useReportsSearch();
  const { scope, applyScope, accountIds } = useScopeSelection({
    scope: search.scope,
    onScopeChange: search.setScope,
    urlHasScope: search.hasScope,
  });
  const { data: metricsDict } = useMetrics(accountIds);
  const dateRange = useMemo(() => dateWindowFromRange(scope.range, today), [scope.range, today]);
  const kpiOptions = useMemo(() => availableKpis(metricsDict), [metricsDict]);

  if (isPending || session === null) {
    return (
      <Box
        sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const kpi = kpiOptions.some((option) => option.id === search.kpi)
    ? search.kpi
    : (kpiOptions[0]?.id ?? '');

  return (
    <AppShell
      active="reports"
      account={account}
      onLogout={() => void onSignOut()}
      topBar={
        <ReportsFilterBar
          scope={scope}
          onScopeChange={applyScope}
          dateRange={dateRange}
          accountIds={accountIds}
          kpi={kpi}
          onKpiChange={search.setKpi}
          kpiOptions={kpiOptions}
          campaigns={search.campaign}
          onCampaignsChange={search.setCampaign}
        />
      }
    >
      <ReportScopeProvider
        scope={{
          dateRange,
          creativeType: scope.creativeType,
          campaignNames: search.campaign,
          kpi,
          accountIds,
        }}
      >
        <Outlet />
      </ReportScopeProvider>
    </AppShell>
  );
}

export const Route = createFileRoute('/reports')({
  component: ReportsLayout,
  validateSearch: validateReportsSearch,
});
